import { Component, OnInit } from '@angular/core';
import { CommonModule } from '@angular/common';
import { Router } from '@angular/router';
import { FormsModule } from '@angular/forms';
import { HttpClient } from '@angular/common/http';
import { firstValueFrom } from 'rxjs';
import { environment } from '../../../environments/environment';

interface DroneManufacturer {
  id: string;
  name: string;
  description: string | null;
  url: string | null;
  logoUrl: string | null;
  modelCount?: number;
  createdAt: string;
  updatedAt: string;
}

interface DroneModel {
  id: string;
  manufacturerId: string;
}

interface ApiResponse<T> {
  success: boolean;
  data: T[];
}

@Component({
  selector: 'app-drone-catalog',
  standalone: true,
  imports: [CommonModule, FormsModule],
  templateUrl: './drone-catalog.component.html',
  styleUrls: ['./drone-catalog.component.scss']
})
export class DroneCatalogComponent implements OnInit {
  manufacturers: DroneManufacturer[] = [];
  filteredManufacturers: DroneManufacturer[] = [];
  loading = true;
  errorMessage = '';

  // Search
  searchQuery = '';

  constructor(
    private http: HttpClient,
    private router: Router
  ) {}

  ngOnInit(): void {
    this.loadManufacturers();
  }

  async loadManufacturers(): Promise<void> {
    this.loading = true;
    this.errorMessage = '';

    try {
      // Fetch manufacturers and models in parallel
      const [manufacturersResponse, modelsResponse] = await Promise.all([
        firstValueFrom(
          this.http.get<ApiResponse<DroneManufacturer>>(
            `${environment.apiUrl}/admin/drone-manufacturers`
          )
        ),
        firstValueFrom(
          this.http.get<ApiResponse<DroneModel>>(
            `${environment.apiUrl}/admin/drone-models`
          )
        )
      ]);

      if (manufacturersResponse.success && manufacturersResponse.data) {
        // Count models per manufacturer
        const modelCounts = new Map<string, number>();
        if (modelsResponse.success && modelsResponse.data) {
          for (const model of modelsResponse.data) {
            const count = modelCounts.get(model.manufacturerId) || 0;
            modelCounts.set(model.manufacturerId, count + 1);
          }
        }

        // Add model counts to manufacturers
        this.manufacturers = manufacturersResponse.data.map(m => ({
          ...m,
          modelCount: modelCounts.get(m.id) || 0
        }));
        this.applyFilter();
      }
    } catch (error: any) {
      console.error('Failed to load manufacturers:', error);
      this.errorMessage = 'Failed to load drone manufacturers. Please try again.';
    } finally {
      this.loading = false;
    }
  }

  applyFilter(): void {
    if (!this.searchQuery.trim()) {
      this.filteredManufacturers = [...this.manufacturers];
    } else {
      const query = this.searchQuery.toLowerCase().trim();
      this.filteredManufacturers = this.manufacturers.filter(m =>
        m.name.toLowerCase().includes(query) ||
        (m.description && m.description.toLowerCase().includes(query))
      );
    }
  }

  onSearch(): void {
    this.applyFilter();
  }

  viewManufacturer(manufacturer: DroneManufacturer): void {
    this.router.navigate(['/drone-catalog', manufacturer.id]);
  }

  createManufacturer(): void {
    this.router.navigate(['/drone-catalog/create']);
  }

  formatDate(date: string): string {
    if (!date) return '-';
    return new Date(date).toLocaleDateString('en-US', {
      year: 'numeric',
      month: 'short',
      day: 'numeric'
    });
  }
}
